import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Building2, Mail, CheckCircle2, Clock, Loader2 } from 'lucide-react';

const API = '/api';

export default function PublicILStatusPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [revoked, setRevoked] = useState({ revoked: false, message: '' });

  useEffect(() => {
    fetch(`${API}/public/india-licensing/${token}`)
      .then(async r => {
        if (r.status === 410) {
          const body = await r.json().catch(() => ({}));
          setRevoked({ revoked: true, message: body.detail || 'This link has been revoked by the account owner.' });
          throw new Error('revoked');
        }
        if (!r.ok) throw new Error('Invalid link');
        return r.json();
      })
      .then(setData)
      .catch(e => { if (e.message !== 'revoked') setError(e.message); })
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <Loader2 className="w-6 h-6 animate-spin text-[#00709B]" />
    </div>
  );

  if (revoked.revoked) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-6" data-testid="public-il-status-revoked">
      <div className="max-w-md text-center bg-white border border-slate-200 rounded-xl shadow-sm p-8">
        <div className="w-12 h-12 mx-auto bg-rose-50 border border-rose-200 rounded-full flex items-center justify-center mb-3">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-rose-600">
            <rect x="3" y="11" width="18" height="11" rx="2"/>
            <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
          </svg>
        </div>
        <p className="text-base font-semibold text-slate-800 mb-2">Access revoked</p>
        <p className="text-[13px] text-slate-600 leading-relaxed">{revoked.message}</p>
      </div>
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <div className="text-center">
        <p className="text-lg font-semibold text-slate-700">Link Expired or Invalid</p>
        <p className="text-sm text-slate-400 mt-1">This share link is no longer accessible.</p>
      </div>
    </div>
  );

  const records = data?.outreach_records || [];
  const respondedCount = records.filter(r => r.responded || r.tagged_as_responder).length;

  return (
    <div className="min-h-screen bg-slate-50" style={{ fontFamily: 'Manrope, sans-serif' }}>
      {/* Header */}
      <div className="bg-[#0A1128] text-white">
        <div className="max-w-4xl mx-auto px-6 py-8">
          <p className="text-[10px] uppercase tracking-[0.2em] text-slate-400 mb-2">Licensing — Outreach Status</p>
          <h1 className="text-2xl font-bold tracking-tight" data-testid="il-share-project-name">{data.project_name}</h1>
          {data.client_company && (
            <div className="flex items-center gap-2 mt-2 text-slate-300 text-sm">
              <Building2 className="w-3.5 h-3.5" />
              <span>{data.client_company}</span>
            </div>
          )}
          <div className="flex gap-3 mt-4">
            <Badge className="bg-white/10 text-white border-white/20 text-xs">{data.status || 'Active'}</Badge>
            <Badge className="bg-white/10 text-white border-white/20 text-xs">{records.length} Outreach Sent</Badge>
            {respondedCount > 0 && (
              <Badge className="bg-white/10 text-white border-white/20 text-xs">{respondedCount} Responded</Badge>
            )}
          </div>
        </div>
      </div>

      {/* Outreach Records */}
      <div className="max-w-4xl mx-auto px-6 py-8 space-y-2" data-testid="il-share-outreach-records">
        {records.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-12">No outreach recorded for this project yet.</p>
        ) : records.map((r, ri) => (
          <div key={ri} className="flex items-center gap-3 text-sm py-2.5 px-4 bg-white rounded-xl border border-slate-200 shadow-sm" data-testid={`il-share-record-${ri}`}>
            <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
              <Mail className="w-3.5 h-3.5 text-slate-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-700 truncate">{r.contact_name || '—'}</p>
              <p className="text-[11px] text-slate-400 truncate">{r.contact_email}</p>
            </div>
            <div className="text-right shrink-0 mr-1">
              <p className="text-[11px] text-slate-400">
                {r.sent_at ? new Date(r.sent_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : ''}
              </p>
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              {r.responded || r.tagged_as_responder ? (
                <span className="flex items-center gap-1 text-[11px] text-green-600 font-medium">
                  <CheckCircle2 className="w-3.5 h-3.5" /> Responded
                </span>
              ) : (
                <span className="flex items-center gap-1 text-[11px] text-slate-400">
                  <Clock className="w-3.5 h-3.5" /> {r.status || 'Sent'}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Footer */}
      <div className="max-w-4xl mx-auto px-6 pb-10 text-center">
        <p className="text-[10px] text-slate-300">Shared via ForesightRx</p>
      </div>
    </div>
  );
}
